import { Type } from "@api-types/bet-booster/BetBoosterHubFilter";
import { BetBoosterHubResponse } from "@api-types/bet-booster/BetBoosterHubResponse";
import { BetBoosterData } from "@data-types/bet-booster";
import { TBetBuilderPick } from "@data-types/bet-booster/BetBuilderPicks";
import type { RootStore } from "@state/stores/types";
import {
	updateSlipIndicator,
	resetIndicatorAfterDelay,
} from "@utils/bet-builder";
import { action, observable, runInAction } from "mobx";
import { Subscription } from "rxjs";
import { BetBuilderPicksStore } from "./BetBuilderPicksStore";
import { THEME_SETTINGS } from "@theme-settings";
import { BetBuilderApiService } from "@api-services/bet-builder/BetBuilderApiService";

export default class BetBoosterEventPickStore {
	rootStore: RootStore;

	@observable isStoreInitialized: boolean = false;
	@observable isAvailable: boolean = false;
	@observable data: Omit<
		BetBoosterData,
		"mostPlayedTicketIds" | "mostPlayedTickets"
	> = {
		betBuilderPresetIds: [],
		betBuilderPresets: new Map<string, TBetBuilderPick>(),
	};
	subscription: Subscription | null = null;
	eventId: string;

	@observable private timeoutMap: Map<string, ReturnType<typeof setTimeout>> =
		new Map();
	@observable betBuilderPicksStore: BetBuilderPicksStore | null = null;
	@observable previousBetBuilderStatus: boolean = false;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
	}

	@action.bound
	async onInitialize(eventId: string, isLive: boolean) {
		this.eventId = eventId;
		if (!WEBPACK_IS_BET_BOOSTER_ENABLED) {
			return;
		}
		if (
			!THEME_SETTINGS.features.quickPlayOptions.includes(
				"bet-builder-picks"
			) &&
			!THEME_SETTINGS.features.quickPlayOptions.includes(
				"most-played-events"
			)
		) {
			this.isStoreInitialized = true;
			return;
		}
		if (isLive) {
			await this.onInitializeLiveEvent();
			return;
		}

		await this.onInitializePrematchEvent();
	}
	@action.bound
	async onInitializeLiveEvent() {
		const data =
			await BetBuilderApiService.getEventBetBuilderPicksAvailability(
				this.eventId
			);

		if (
			data.statusCode != 200 ||
			(data.response && data.response?.length < 1)
		) {
			this.isAvailable = false;
			return;
		}

		this.isAvailable = true;
		this.betBuilderPicksStore = new BetBuilderPicksStore(this.rootStore);
		this.getBetBuilderPicks();
	}

	@action.bound
	async onInitializePrematchEvent() {
		//@ts-expect-error
		const mappedData: BetBoosterHubResponse = {
			betBuilderPresetIds: [],
			betBuilderPresets: [],
			mostPlayedTicketIds: [],
			mostPlayedTickets: [],
		};
		this.isStoreInitialized = true;

		try {
			const bbpData =
				await BetBuilderApiService.getEventBetBuilderPicksAvailability(
					this.eventId
				);

			if (
				bbpData.statusCode != 200 ||
				(bbpData.response && bbpData.response?.length < 1)
			) {
				throw "No bbpicks";
			}
			const bbpIds = bbpData.response?.map((slip) => {
				//@ts-expect-error
				mappedData.betBuilderPresets.push(slip);
				return slip.id;
			});
			//@ts-expect-error
			mappedData.betBuilderPresetIds = bbpIds;
		} catch (error) {
			console.error(error);
			this.isAvailable = false;
			return;
		}

		this.betBuilderPicksStore = new BetBuilderPicksStore(this.rootStore);
		this.handleResponse(mappedData);
		this.isAvailable = true;
	}
	@action.bound
	getBetBuilderPicks() {
		this.disposeSubscription();
		if (this.rootStore.betBoosterHubStore.isSessionExpired) {
			this.rootStore.betBoosterHubStore.restartSession();
		}
		this.subscription = this.rootStore.betBoosterHubStore.hub
			.getSubscription({
				subscriptionId: "bet-booster-event-" + this.eventId,
				shopId: this.rootStore.userAuthStore.shopId,
				eventIds: [this.eventId],
				type: Type["bb-presets"],
			})
			.subscribe({
				next: (data) => {
					this.handleResponse(data);
				},
				error: (e) => {
					console.error(e);
				},
			});

		this.isAvailable = true;
	}

	@action.bound
	handleResponse(data: BetBoosterHubResponse) {
		if (data.betBuilderPresetIds) {
			this.data.betBuilderPresetIds = data.betBuilderPresetIds;
		} else {
			this.data.betBuilderPresetIds = [];
		}

		if (this.data.betBuilderPresetIds.length) {
			let bbMap = new Map<string, TBetBuilderPick>();

			data.betBuilderPresets?.forEach((slip: TBetBuilderPick) => {
				const existingSlip = this.data.betBuilderPresets.get(slip.id);

				updateSlipIndicator(slip, existingSlip);

				const existingTimeoutId = this.timeoutMap.get(slip.id);
				if (existingTimeoutId) {
					clearTimeout(existingTimeoutId);
				}

				resetIndicatorAfterDelay(
					slip,
					3000,
					(updatedSlip) => {
						runInAction(() => {
							this.betBuilderPicksStore?.updateBetBuilderPick(
								updatedSlip
							);
							this.data.betBuilderPresets.set(
								updatedSlip.id,
								updatedSlip
							);
						});
					},
					this.timeoutMap
				);

				bbMap.set(slip.id, slip);
				const isValidMulitOffer = slip.multiOffers.every(
					(mo) => mo.sportData != null
				);
				if (!isValidMulitOffer) {
					this.data.betBuilderPresetIds =
						this.data.betBuilderPresetIds.filter(
							(id) => slip.id != id
						);
				}
			});

			this.data.betBuilderPresets.forEach((oldSlip) => {
				if (!bbMap.has(oldSlip.id)) {
					bbMap.set(oldSlip.id, oldSlip);
				}
			});

			this.data.betBuilderPresets = bbMap;
		} else {
			this.data.betBuilderPresets.clear();
		}
		this.betBuilderPicksStore?.onInitializeBetBuilderPicks(this.data);
		this.isStoreInitialized = true;
	}

	@action.bound
	disposeSubscription() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	onDispose() {
		this.isStoreInitialized = false;
		this.isAvailable = false;
		this.disposeSubscription();
		this.data.betBuilderPresetIds = [];
		this.data.betBuilderPresets.clear();
		this.previousBetBuilderStatus = false;
		this.timeoutMap.clear();
		this.betBuilderPicksStore?.onDispose();
	}
}
